import _ from "lodash";

import * as entityUtils from "./entityUtils";
import {
	ASSET_FOLDER_STRUCTURE_ITEMS,
	ASSET_FOLDER_STRUCTURE_LIST_SECTIONS,
	ASSET_FOLDER_STRUCTURE_SECTIONS
} from "./assetFolderImportUtils";
import { generateUUID } from "./utils";

export const POWERPOINT_CONVERSION_SLIDE_IMAGE = "slideImage";
export const POWERPOINT_CONVERSION_HYBRID = "hybrid";
export const POWERPOINT_CONVERSION_MARKDOWN_MIMIC = "markdownMimic";
export const POWERPOINT_CONVERSION_NATIVE = "native";
export const POWERPOINT_CONVERSION_SKIP = "skip";
export const POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_BULLETS = "bullets";
export const POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_PREFIXES = "prefixes";

const POWERPOINT_METADATA_NAME_LIMIT = 512;
const POWERPOINT_METADATA_PATH_LIMIT = 2048;
const POWERPOINT_METADATA_DATE_LIMIT = 128;

export const POWERPOINT_CONVERSION_OPTIONS = [
	{
		value: POWERPOINT_CONVERSION_HYBRID,
		label: "Hybrid",
		description: "Use rendered slide image when available, plus native Label 1 and markdown Label 2."
	},
	{
		value: POWERPOINT_CONVERSION_MARKDOWN_MIMIC,
		label: "Markdown Mimic",
		description: "Use rendered slide image when available, plus Label 2 markdown that preserves detected bullet indentation."
	},
	{
		value: POWERPOINT_CONVERSION_SLIDE_IMAGE,
		label: "Slide Image",
		description: "Use the rendered slide image as the item media and keep extracted content as metadata."
	},
	{
		value: POWERPOINT_CONVERSION_NATIVE,
		label: "Native Labels",
		description: "Create native AmbiFi label content from slide title and text without requiring a slide image."
	},
	{
		value: POWERPOINT_CONVERSION_SKIP,
		label: "Skip",
		description: "Do not import this slide."
	}
];

export const POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_OPTIONS = [
	{
		value: POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_BULLETS,
		label: "Normal bullets",
		description: "Convert detected bullets and numbering into standard nested markdown bullets."
	},
	{
		value: POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_PREFIXES,
		label: "Preserve prefixes",
		description: "Attempt to preserve PowerPoint numbering and lettering prefixes like 1., a., b."
	}
];

function cleanLine(value = "") {
	return `${value || ""}`.replace(/\s+/g, " ").trim();
}

function compactMetadataString(value = "", limit = POWERPOINT_METADATA_NAME_LIMIT) {
	const text = `${value || ""}`;
	return text.length > limit ? text.substring(0, limit) : text;
}

function getFiniteMetadataNumber(value, fallback = null) {
	const number = Number(value);
	return Number.isFinite(number) ? number : fallback;
}

function getMetadataArrayCount(source = {}, arrayKey = "", countKey = "") {
	if (Array.isArray(source[arrayKey])) {
		return source[arrayKey].length;
	}
	const count = getFiniteMetadataNumber(source[countKey], 0);
	return Math.max(0, Math.floor(count));
}

function getSlideTitle(slide = {}, fallbackIndex = 0) {
	return cleanLine(slide.label1 || slide.title || _.get(slide, "sourcePresentationSlide.title", "")) || `Slide ${Number(slide.index) || fallbackIndex + 1}`;
}

function isSameLine(a = "", b = "") {
	return cleanLine(a).toLowerCase() === cleanLine(b).toLowerCase();
}

function stripMarkdownListPrefix(value = "") {
	return cleanLine(value).replace(/^(?:[-*+]|\d+[.)])\s+/, "").trim();
}

function clampMarkdownLevel(value) {
	const parsed = Number(value);
	if (!Number.isFinite(parsed) || parsed < 0) {
		return 0;
	}
	return Math.min(8, Math.floor(parsed));
}

function getStructuredParagraphs(slide = {}) {
	if (Array.isArray(slide.paragraphs) && slide.paragraphs.length > 0) {
		return slide.paragraphs;
	}
	if (Array.isArray(slide.shapes) && slide.shapes.length > 0) {
		return slide.shapes.reduce((results, shape) => results.concat(shape.paragraphs || []), []);
	}
	return `${slide.text || ""}`
		.split(/\r?\n/)
		.map((text, index) => ({
			index,
			text,
			level: 0,
			bullet: true,
			numbered: false,
			placeholderType: ""
		}));
}

function isTitleParagraph(paragraph = {}, title = "", index = 0) {
	const placeholderType = `${paragraph.placeholderType || ""}`;
	if (["title", "ctrTitle"].includes(placeholderType) && isSameLine(paragraph.text, title)) {
		return true;
	}
	return index === 0 && isSameLine(paragraph.text, title);
}

function isNonContentPlaceholderParagraph(paragraph = {}) {
	return ["dt", "ftr", "sldNum"].includes(`${paragraph.placeholderType || ""}`);
}

function numberToLetters(value = 1) {
	let remaining = Math.max(1, Number(value) || 1);
	let result = "";
	while (remaining > 0) {
		remaining -= 1;
		result = String.fromCharCode(97 + (remaining % 26)) + result;
		remaining = Math.floor(remaining / 26);
	}
	return result;
}

function numberToRoman(value = 1) {
	let remaining = Math.max(1, Number(value) || 1);
	const romanValues = [
		[1000, "m"],
		[900, "cm"],
		[500, "d"],
		[400, "cd"],
		[100, "c"],
		[90, "xc"],
		[50, "l"],
		[40, "xl"],
		[10, "x"],
		[9, "ix"],
		[5, "v"],
		[4, "iv"],
		[1, "i"]
	];
	let result = "";
	romanValues.forEach(([arabic, roman]) => {
		while (remaining >= arabic) {
			result += roman;
			remaining -= arabic;
		}
	});
	return result || "i";
}

function formatAutoNumberPrefix(value = 1, numberingType = "") {
	const type = `${numberingType || ""}`;
	const lowerType = type.toLowerCase();
	let token = `${Math.max(1, Number(value) || 1)}`;
	if (lowerType.indexOf("alpha") >= 0) {
		token = numberToLetters(value);
		if (type.indexOf("Uc") >= 0 || type.indexOf("UC") >= 0) {
			token = token.toUpperCase();
		}
	} else if (lowerType.indexOf("roman") >= 0) {
		token = numberToRoman(value);
		if (type.indexOf("Uc") >= 0 || type.indexOf("UC") >= 0) {
			token = token.toUpperCase();
		}
	}

	if (lowerType.indexOf("parenboth") >= 0) {
		return `(${token})`;
	}
	if (lowerType.indexOf("parenr") >= 0) {
		return `${token})`;
	}
	return `${token}.`;
}

function getCounterScope(paragraph = {}) {
	return paragraph.shapeId || paragraph.shapeName || `${paragraph.shapeIndex || 0}`;
}

function resetDeeperCounters(countersByKey, paragraph = {}, level = 0) {
	const scope = getCounterScope(paragraph);
	Object.keys(countersByKey).forEach((key) => {
		const [counterScope, counterLevel] = key.split("|");
		if (counterScope === scope && Number(counterLevel) > level) {
			delete countersByKey[key];
		}
	});
}

function getPreservedPrefix(paragraph = {}, level = 0, countersByKey = {}) {
	if (!paragraph.numbered) {
		return "-";
	}

	const scope = getCounterScope(paragraph);
	const numberingType = paragraph.numberingType || "arabicPeriod";
	const key = `${scope}|${level}|${numberingType}`;
	if (!countersByKey[key]) {
		countersByKey[key] = Number(paragraph.numberingStartAt) || 1;
	} else {
		countersByKey[key] += 1;
	}
	return formatAutoNumberPrefix(countersByKey[key], numberingType);
}

export function buildMarkdownMimicFromSlide(slide = {}, options = {}) {
	const title = getSlideTitle(slide);
	const paragraphs = getStructuredParagraphs(slide)
		.map((paragraph, index) => ({ ...paragraph, originalIndex: index, text: cleanLine(paragraph.text) }))
		.filter((paragraph) => paragraph.text !== "")
		.filter((paragraph) => !isNonContentPlaceholderParagraph(paragraph))
		.filter((paragraph, index) => !isTitleParagraph(paragraph, title, index));

	if (paragraphs.length === 0) {
		return "";
	}

	const listStyle = options.markdownMimicListStyle || slide.markdownMimicListStyle || POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_BULLETS;
	const rawLevels = paragraphs.map((paragraph) => clampMarkdownLevel(paragraph.level));
	const minLevel = rawLevels.length > 0 ? Math.min(...rawLevels) : 0;
	const countersByKey = {};
	return paragraphs.map((paragraph) => {
		const level = Math.max(0, clampMarkdownLevel(paragraph.level) - minLevel);
		resetDeeperCounters(countersByKey, paragraph, level);
		const prefix = listStyle === POWERPOINT_MARKDOWN_MIMIC_LIST_STYLE_PREFIXES
			? getPreservedPrefix(paragraph, level, countersByKey)
			: "-";
		return `${"  ".repeat(level)}${prefix} ${stripMarkdownListPrefix(paragraph.text)}`;
	}).join("\n");
}

function buildFlatMarkdownFromSlide(slide = {}) {
	const title = getSlideTitle(slide);
	const lines = `${slide.text || ""}`
		.split(/\r?\n/)
		.map(cleanLine)
		.filter(Boolean)
		.filter((line, index) => !(index === 0 && isSameLine(line, title)));

	if (lines.length === 0) {
		return "";
	}

	return lines.map((line) => (/^(?:[-*+]|\d+[.)])\s+/.test(line) ? line : `- ${line}`)).join("\n");
}

function usesMarkdownMimic(conversionMode = "") {
	return conversionMode === POWERPOINT_CONVERSION_MARKDOWN_MIMIC;
}

function usesNativeText(conversionMode = "") {
	return conversionMode === POWERPOINT_CONVERSION_HYBRID
		|| conversionMode === POWERPOINT_CONVERSION_MARKDOWN_MIMIC
		|| conversionMode === POWERPOINT_CONVERSION_NATIVE;
}

function usesBackgroundSlideImage(conversionMode = "") {
	return conversionMode === POWERPOINT_CONVERSION_HYBRID
		|| conversionMode === POWERPOINT_CONVERSION_MARKDOWN_MIMIC;
}

export function buildMarkdownFromSlide(slide = {}, options = {}) {
	const conversionMode = options.conversionMode || slide.conversionMode || POWERPOINT_CONVERSION_HYBRID;
	if (!usesNativeText(conversionMode)) {
		return "";
	}

	const explicitLabel2 = `${slide.label2 || ""}`.trim();
	if (options.useExistingLabel2 !== false && explicitLabel2) {
		return explicitLabel2;
	}

	if (usesMarkdownMimic(conversionMode)) {
		const mimicMarkdown = buildMarkdownMimicFromSlide(slide, options);
		if (mimicMarkdown) {
			return mimicMarkdown;
		}
	}
	return buildFlatMarkdownFromSlide(slide);
}

function buildSourcePresentationMetadata(config = {}) {
	const manifest = config.sourcePresentationManifest || {};
	const deck = manifest.deck || {};
	const driveObject = config.sourcePresentationDriveObject || {};
	const slideCount = deck.slideCount || (Array.isArray(manifest.slides) ? manifest.slides.length : 0);
	return {
		source: "pptx",
		fileName: compactMetadataString(deck.fileName || _.get(config, "sourcePresentationFile.name", "")),
		title: compactMetadataString(deck.title || ""),
		slideCount: Math.max(0, Math.floor(getFiniteMetadataNumber(slideCount, 0))),
		created: compactMetadataString(deck.created || "", POWERPOINT_METADATA_DATE_LIMIT),
		modified: compactMetadataString(deck.modified || "", POWERPOINT_METADATA_DATE_LIMIT),
		creator: compactMetadataString(deck.creator || ""),
		lastModifiedBy: compactMetadataString(deck.lastModifiedBy || ""),
		widthEmu: getFiniteMetadataNumber(deck.widthEmu),
		heightEmu: getFiniteMetadataNumber(deck.heightEmu),
		widthInches: getFiniteMetadataNumber(deck.widthInches),
		heightInches: getFiniteMetadataNumber(deck.heightInches),
		aspectRatio: getFiniteMetadataNumber(deck.aspectRatio),
		driveObjectGuid: compactMetadataString(driveObject.guid || ""),
		driveObjectPath: compactMetadataString(driveObject.path || "", POWERPOINT_METADATA_PATH_LIMIT)
	};
}

function buildSlideMetadata(slide = {}) {
	// Item labels and the uploaded source presentation retain the actual slide
	// content. Keep only bounded provenance and diagnostic counts here. In
	// particular, assets can contain base64 previews that are far too large to
	// duplicate into every item's authorProperties.
	return {
		index: getFiniteMetadataNumber(slide.index),
		slideId: compactMetadataString(slide.slideId || ""),
		relationshipId: compactMetadataString(slide.relationshipId || ""),
		path: compactMetadataString(slide.path || "", POWERPOINT_METADATA_PATH_LIMIT),
		fileName: compactMetadataString(slide.fileName || ""),
		title: compactMetadataString(slide.title || ""),
		textLength: `${slide.text || ""}`.length,
		notesLength: `${slide.notes || ""}`.length,
		paragraphCount: getMetadataArrayCount(slide, "paragraphs", "paragraphCount"),
		slideParagraphCount: getMetadataArrayCount(slide, "slideParagraphs", "slideParagraphCount"),
		inheritedParagraphCount: getMetadataArrayCount(slide, "inheritedParagraphs", "inheritedParagraphCount"),
		relatedTextPartCount: getMetadataArrayCount(slide, "relatedTextParts", "relatedTextPartCount"),
		assetCount: getMetadataArrayCount(slide, "assets", "assetCount"),
		hyperlinkCount: getMetadataArrayCount(slide, "hyperlinks", "hyperlinkCount"),
		shapeCount: getMetadataArrayCount(slide, "shapes", "shapeCount")
	};
}

function buildPowerPointAuthorProperties(slide = {}, config = {}, imageAsset = null) {
	return {
		powerPointImport: {
			conversionMode: compactMetadataString(slide.conversionMode || config.defaultConversionMode || POWERPOINT_CONVERSION_HYBRID),
			markdownMimicListStyle: compactMetadataString(config.markdownMimicListStyle || slide.markdownMimicListStyle || ""),
			importedAt: new Date().toISOString(),
			imageDriveObjectGuid: compactMetadataString(_.get(imageAsset, "driveObject.guid", "")),
			imageDriveObjectPath: compactMetadataString(_.get(imageAsset, "driveObject.path", ""), POWERPOINT_METADATA_PATH_LIMIT)
		},
		sourcePresentation: buildSourcePresentationMetadata(config),
		sourcePresentationSlide: buildSlideMetadata(slide)
	};
}

function getSlideImageAsset(slide = {}, imageAssetsBySlideIndex = {}) {
	const index = Number(slide.index);
	return Number.isFinite(index) ? imageAssetsBySlideIndex[index] : null;
}

export function normalizePowerPointSlidesForImport(manifest = null, options = {}) {
	const slides = manifest && Array.isArray(manifest.slides) ? manifest.slides : [];
	const defaultConversionMode = options.defaultConversionMode || POWERPOINT_CONVERSION_HYBRID;
	const defaultListName = options.defaultListName || _.get(manifest, "deck.title", "PowerPoint Slides") || "PowerPoint Slides";
	const defaultSectionName = options.defaultSectionName || _.get(manifest, "deck.title", "Slides") || "Slides";

	return slides.map((slide, index) => {
		const normalizedSlide = {
			...slide,
			id: slide.id || generateUUID(),
			index: Number(slide.index) || index + 1,
			conversionMode: defaultConversionMode,
			label1: slide.title || `Slide ${Number(slide.index) || index + 1}`,
			label2: "",
			label2Auto: true,
			listStart: index === 0,
			listTitle: index === 0 ? defaultListName : "",
			sectionStart: index === 0,
			sectionTitle: index === 0 ? defaultSectionName : ""
		};
		normalizedSlide.label2 = buildMarkdownFromSlide(normalizedSlide, {
			conversionMode: defaultConversionMode,
			markdownMimicListStyle: options.markdownMimicListStyle,
			useExistingLabel2: false
		});
		return normalizedSlide;
	});
}

export function getPowerPointSlideImageAssets(slideImageAssets = []) {
	return (Array.isArray(slideImageAssets) ? slideImageAssets : [])
		.filter((asset) => asset && asset.sourcePresentationSlide && Number.isFinite(Number(asset.sourcePresentationSlide.index)));
}

export function getPowerPointSlideImageAssetsBySlideIndex(slideImageAssets = []) {
	return _.keyBy(getPowerPointSlideImageAssets(slideImageAssets), (asset) => Number(asset.sourcePresentationSlide.index));
}

export function buildPowerPointItemNode(slide = {}, config = {}) {
	const conversionMode = slide.conversionMode || config.defaultConversionMode || POWERPOINT_CONVERSION_HYBRID;
	if (conversionMode === POWERPOINT_CONVERSION_SKIP) {
		return null;
	}

	const imageAssetsBySlideIndex = config.imageAssetsBySlideIndex || getPowerPointSlideImageAssetsBySlideIndex(config.slideImageAssets);
	const imageAsset = getSlideImageAsset(slide, imageAssetsBySlideIndex);
	const hasImageAsset = !!imageAsset;
	const includeNativeText = usesNativeText(conversionMode);
	const label1 = getSlideTitle(slide, Number(slide.index) - 1);
	const label2 = includeNativeText ? buildMarkdownFromSlide(slide, {
		conversionMode,
		markdownMimicListStyle: config.markdownMimicListStyle
	}) : "";
	const labelOnly = config.labelOnly !== false;
	const itemEntity = entityUtils.addItemEntity(generateUUID(), label1, label2, labelOnly, false, "", "item");
	const createAssetReference = _.isFunction(config.createAssetReference) ? config.createAssetReference : null;
	const resourceReference = hasImageAsset && createAssetReference ? createAssetReference(imageAsset, config) : _.get(imageAsset, "resourceReference", null);

	if (resourceReference) {
		itemEntity.image = resourceReference;
	}
	if (usesBackgroundSlideImage(conversionMode) && resourceReference) {
		itemEntity.imageShowInBackground = true;
		itemEntity.imageResizeMode = "contain";
		itemEntity.imageMaximumHeight = 100;
		itemEntity.imageMaximumHeightAnchor = "top";
	}
	itemEntity.authorProperties = JSON.stringify(buildPowerPointAuthorProperties(slide, config, imageAsset), null, 2);
	return entityUtils.addItemNode(itemEntity);
}

function groupSlidesBySections(slides = [], defaultSectionName = "Slides") {
	const groups = [];
	let activeGroup = null;
	slides.forEach((slide) => {
		if (!activeGroup || slide.sectionStart === true) {
			activeGroup = {
				label: cleanLine(slide.sectionTitle) || (groups.length === 0 ? defaultSectionName : `Section ${groups.length + 1}`),
				slides: []
			};
			groups.push(activeGroup);
		}
		activeGroup.slides.push(slide);
	});
	return groups;
}

function groupSlidesByLists(slides = [], defaultListName = "PowerPoint Slides") {
	const groups = [];
	let activeGroup = null;
	slides.forEach((slide) => {
		if (!activeGroup || slide.listStart === true) {
			activeGroup = {
				label: cleanLine(slide.listTitle) || (groups.length === 0 ? defaultListName : `List ${groups.length + 1}`),
				slides: []
			};
			groups.push(activeGroup);
		}
		activeGroup.slides.push(slide);
	});
	return groups;
}

export function buildPowerPointSectionNodes(slides = [], config = {}) {
	const sectionLabel = cleanLine(config.sectionLabel || config.collectionLabel || _.get(config, "sourcePresentationManifest.deck.title", "Slides")) || "Slides";
	return groupSlidesBySections(slides, sectionLabel).map((group) => {
		const sectionEntity = entityUtils.addSectionEntity(generateUUID(), group.label);
		const sectionNode = entityUtils.addCategoryNode(sectionEntity, true);
		sectionNode.children = group.slides
			.map((slide) => buildPowerPointItemNode(slide, config))
			.filter(Boolean);
		return sectionNode;
	}).filter((sectionNode) => sectionNode.children.length > 0);
}

export function buildPowerPointImportNodes(config = {}) {
	const slides = (Array.isArray(config.slides) ? config.slides : [])
		.filter((slide) => slide && slide.conversionMode !== POWERPOINT_CONVERSION_SKIP);
	const imageAssetsBySlideIndex = getPowerPointSlideImageAssetsBySlideIndex(config.slideImageAssets);
	const buildConfig = {
		...config,
		imageAssetsBySlideIndex
	};
	const structurePreset = config.structurePreset || ASSET_FOLDER_STRUCTURE_SECTIONS;

	if (structurePreset === ASSET_FOLDER_STRUCTURE_ITEMS) {
		return slides.map((slide) => buildPowerPointItemNode(slide, buildConfig)).filter(Boolean);
	}

	if (structurePreset === ASSET_FOLDER_STRUCTURE_LIST_SECTIONS) {
		const defaultListLabel = cleanLine(config.collectionLabel || _.get(config, "sourcePresentationManifest.deck.title", "PowerPoint Slides")) || "PowerPoint Slides";
		return groupSlidesByLists(slides, defaultListLabel).map((group) => {
			const listEntity = entityUtils.addListEntity(generateUUID(), group.label);
			const listNode = entityUtils.addCategoryNode(listEntity, true);
			listNode.children = buildPowerPointSectionNodes(group.slides, {
				...buildConfig,
				sectionLabel: cleanLine(config.sectionLabel) || group.label
			});
			return listNode;
		}).filter((listNode) => listNode.children.length > 0);
	}

	return buildPowerPointSectionNodes(slides, buildConfig);
}
